import { Link, useLocalSearchParams } from 'expo-router'
import { useCallback, useMemo } from 'react'
import { FlatList, StyleSheet, View } from 'react-native'
import { Button, Divider, Icon, Text, useTheme } from 'react-native-paper'
import { DEFAULT_PERIOD_TYPE, DEFAULT_WEEK_START_DAY } from '@/constants/periods'
import { useApprovedJobEntries } from '@/hooks/useApprovedJobEntries'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useJobRecord } from '@/hooks/useJobRecord'
import { useOrgSettings } from '@/hooks/useOrgSettings'
import { usePeriodNavigation } from '@/hooks/usePeriodNavigation'
import { isNonBlankAddress } from '@/lib/address'
import {
    formatDateShort,
    formatDurationHHMM,
    getNetWorkSeconds,
    sumNetWorkSeconds,
} from '@/lib/dates'
import { displayLocationName } from '@/lib/location-utils'
import { isTravelRouteNotes } from '@/lib/movement-utils'
import { getPeriodBounds, getPeriodInfo } from '@/lib/periods'
import type { Location } from '@/types/locations'
import type { PeriodConfig, WeekStartDay } from '@/types/periods'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { AddressPanel } from '@/ui/AddressPanel'
import { Card } from '@/ui/Card'
import { CollapsibleSection } from '@/ui/CollapsibleSection'
import { EmptyCard } from '@/ui/EmptyCard'
import { ListWrapper } from '@/ui/ListWrapper'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { LocationCard } from '@/ui/locations/LocationCard'
import { NotFound } from '@/ui/NotFound'
import { PeriodNavigator } from '@/ui/PeriodNavigator'

function getEntryTypeIcon(entryType: string) {
    switch (entryType) {
        case 'manual':
            return 'pencil'
        case 'timer':
            return 'timer-outline'
        case 'geofence':
            return 'map-marker-radius'
        case 'travel':
            return 'car'
        default:
            return 'circle-outline'
    }
}

function calculateTotalSeconds(entries: TimeEntriesExpanded[]): number {
    return sumNetWorkSeconds(entries)
}

export default function JobDetailPage() {
    const params = useLocalSearchParams<{ orgSlug: string; jobSlug: string }>()
    const theme = useTheme()
    const { isLoading, job, locations, customer } = useJobRecord(params.jobSlug)
    const { settings } = useOrgSettings(params.orgSlug)

    const periodConfig = useMemo<PeriodConfig>(
        () => ({
            periodType: settings?.period_type || DEFAULT_PERIOD_TYPE,
            weekStartDay: (settings?.week_start_day ?? DEFAULT_WEEK_START_DAY) as WeekStartDay,
        }),
        [settings?.period_type, settings?.week_start_day]
    )

    const {
        periodDate,
        handlePrevious: handlePreviousPeriod,
        handleNext: handleNextPeriod,
        handleDatePicked,
    } = usePeriodNavigation(periodConfig)

    const { start, end } = getPeriodBounds(periodDate, periodConfig)
    const periodInfo = getPeriodInfo(periodDate, periodConfig)

    const { entries, isLoading: isLoadingEntries } = useApprovedJobEntries({
        jobId: job?.id,
        enabled: !!job,
        startDate: start,
        endDate: end,
    })

    useHeaderOptions({
        buttons: { type: 'edit', href: `/${params.orgSlug}/settings/jobs/${job?.identifier}/edit` },
        title: job?.name || job?.identifier,
        enabled: !!job,
    })
    const totalSeconds = calculateTotalSeconds(entries)

    const renderHeader = useCallback(() => {
        if (!job) return null

        const address = job.expand?.address
        const createdDate = new Date(job.created).toLocaleDateString()
        const hasAddress = isNonBlankAddress(address)

        const rightContent = (
            <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                {customer ? customer.name : `Created ${createdDate}`}
            </Text>
        )

        return (
            <View>
                <Card style={styles.collapsibleCard}>
                    <CollapsibleSection
                        title={job.name || job.identifier}
                        icon="briefcase"
                        isLast
                        rightContent={rightContent}
                    >
                        {hasAddress && <AddressPanel address={address} showDivider={false} />}
                    </CollapsibleSection>

                    {job.notes && (
                        <View style={styles.notesSection}>
                            <Divider />
                            <View style={styles.noteContent}>
                                <Text
                                    variant="bodySmall"
                                    style={{ color: theme.colors.onSurfaceVariant }}
                                >
                                    Notes
                                </Text>
                                <Text variant="bodyMedium">{job.notes}</Text>
                            </View>
                        </View>
                    )}
                </Card>

                <Card style={styles.locationsCard}>
                    <CollapsibleSection
                        title="Locations"
                        icon="map"
                        isLast
                        collapsible={locations.length > 0}
                        rightContent={
                            <Text
                                variant="bodySmall"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                {locations.length} location{locations.length !== 1 ? 's' : ''}
                            </Text>
                        }
                        rightControls={
                            <Link
                                href={`/${params.orgSlug}/settings/jobs/${job.identifier}/locations/new`}
                                asChild
                            >
                                <Button mode="text" compact>
                                    Add
                                </Button>
                            </Link>
                        }
                    >
                        <View style={styles.locationsContent}>
                            {locations.map(location => (
                                <LocationCard
                                    key={location.id}
                                    location={location as unknown as Location}
                                />
                            ))}
                        </View>
                    </CollapsibleSection>
                </Card>

                <PeriodNavigator
                    periodLabel={periodInfo.label}
                    onPrevious={handlePreviousPeriod}
                    onNext={handleNextPeriod}
                    periodDate={periodDate}
                    onDatePicked={handleDatePicked}
                />

                {entries.length > 0 && (
                    <View
                        style={[
                            styles.headerRow,
                            { borderBottomColor: theme.colors.outlineVariant },
                        ]}
                    >
                        <View style={styles.typeColumn} />
                        <Text
                            style={[
                                styles.headerText,
                                styles.userColumn,
                                { color: theme.colors.onSurfaceVariant },
                            ]}
                        >
                            User
                        </Text>
                        <Text
                            style={[
                                styles.headerText,
                                styles.locationColumn,
                                { color: theme.colors.onSurfaceVariant },
                            ]}
                        >
                            Location
                        </Text>
                        <Text
                            style={[
                                styles.headerText,
                                styles.dateColumn,
                                { color: theme.colors.onSurfaceVariant },
                            ]}
                        >
                            Date
                        </Text>
                        <Text
                            style={[
                                styles.headerText,
                                styles.hoursColumn,
                                { color: theme.colors.primary },
                            ]}
                        >
                            {formatDurationHHMM(totalSeconds)}
                        </Text>
                    </View>
                )}
            </View>
        )
    }, [
        job,
        customer,
        locations,
        params.orgSlug,
        theme,
        periodInfo.label,
        handlePreviousPeriod,
        handleNextPeriod,
        handleDatePicked,
        periodDate,
        entries.length,
        totalSeconds,
    ])

    const renderItem = useCallback(
        ({ item: entry }: { item: TimeEntriesExpanded }) => {
            const userName = entry.expand?.user?.name || 'Unknown'
            const isTravelWithRoute =
                entry.entry_type === 'travel' && isTravelRouteNotes(entry.notes)
            const locationName = isTravelWithRoute
                ? entry.notes?.split('\n')[0]
                : displayLocationName(entry.expand?.location?.name) || '—'
            const workSeconds = getNetWorkSeconds(entry)

            return (
                <View style={[styles.row, { borderBottomColor: theme.colors.surfaceVariant }]}>
                    <View style={styles.typeColumn}>
                        <Icon
                            source={getEntryTypeIcon(entry.entry_type)}
                            size={14}
                            color={theme.colors.onSurfaceVariant}
                        />
                    </View>
                    <Text
                        style={[
                            styles.cellText,
                            styles.userColumn,
                            { color: theme.colors.onSurface },
                        ]}
                        numberOfLines={1}
                    >
                        {userName}
                    </Text>
                    <Text
                        style={[
                            styles.cellText,
                            styles.locationColumn,
                            { color: theme.colors.onSurfaceVariant },
                        ]}
                        numberOfLines={1}
                    >
                        {locationName}
                    </Text>
                    <Text
                        style={[
                            styles.cellText,
                            styles.dateColumn,
                            { color: theme.colors.onSurface },
                        ]}
                        numberOfLines={1}
                    >
                        {formatDateShort(entry.clock_in)}
                    </Text>
                    <Text
                        style={[
                            styles.hoursText,
                            styles.hoursColumn,
                            { color: theme.colors.primary },
                        ]}
                        numberOfLines={1}
                    >
                        {formatDurationHHMM(workSeconds)}
                    </Text>
                </View>
            )
        },
        [theme]
    )

    const renderEmpty = useCallback(
        () => (
            <EmptyCard
                icon="timetable"
                title="No approved time entries"
                subtitle="Approved time entries for this job will appear here"
            />
        ),
        []
    )

    if (isLoading) {
        return (
            <ListWrapper>
                <LoadingIndicator />
            </ListWrapper>
        )
    }

    if (!job) {
        return (
            <NotFound message="Job not found. It may have been deleted or you don't have permission to view it." />
        )
    }

    if (isLoadingEntries) {
        return (
            <ListWrapper>
                <LoadingIndicator />
            </ListWrapper>
        )
    }

    return (
        <ListWrapper>
            <FlatList
                data={entries}
                keyExtractor={item => item.id}
                renderItem={renderItem}
                ListHeaderComponent={renderHeader}
                ListEmptyComponent={renderEmpty}
                contentContainerStyle={styles.listContent}
            />
        </ListWrapper>
    )
}

const styles = StyleSheet.create({
    listContent: {
        paddingBottom: 24,
    },
    collapsibleCard: {
        overflow: 'hidden',
    },
    notesSection: {
        paddingBottom: 12,
    },
    noteContent: {
        paddingHorizontal: 16,
        paddingTop: 12,
        gap: 4,
    },
    locationsCard: {
        overflow: 'hidden',
    },
    locationsContent: {
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderBottomWidth: 1,
    },
    headerText: {
        fontSize: 11,
        fontWeight: '600',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderBottomWidth: StyleSheet.hairlineWidth,
    },
    typeColumn: {
        width: 28,
        justifyContent: 'center',
        alignItems: 'center',
    },
    userColumn: {
        flex: 1,
        paddingRight: 4,
    },
    locationColumn: {
        flex: 1,
        paddingRight: 4,
    },
    dateColumn: {
        width: 50,
        paddingRight: 4,
    },
    hoursColumn: {
        width: 46,
        textAlign: 'right',
    },
    cellText: {
        fontSize: 12,
    },
    hoursText: {
        fontSize: 12,
        fontWeight: '600',
    },
})
